import { safeGetItem, safeRemoveItem, safeSetItem } from './safeStorage';

export const ADS_CLICK_KEY = 'thisway_ads_click';
const GTAG_SCRIPT_ID = 'thisway-gtag';

// Google Ads click ids in the order the storefront prefers them: gclid
// (web clicks), then gbraid / wbraid (iOS app-to-web / web-to-app clicks).
export const CLICK_TYPES = ['gclid', 'gbraid', 'wbraid'];
// Same check as GoogleAdsPolicy.NormalizeClickId on the API.
const CLICK_ID = /^[A-Za-z0-9_.-]{8,255}$/;
// Google keeps its own click cookies 90 days and its click-through window
// is at most 90 days (spec 2026-10-11 §1.2).
export const ADS_CLICK_TTL_MS = 90 * 24 * 60 * 60 * 1000;
// First path segments that are routes, not a store (routes.config).
const NON_STORE_SEGMENTS = new Set([
  'home',
  'terms',
  'privacy',
  'login',
  'forgot-password',
  'complete',
  'user',
]);

export const normalizeClickId = (raw) => {
  const value = (raw || '').trim();
  if (!value || !CLICK_ID.test(value)) return null;
  return value;
};

export const landingSlugFromPath = (pathname) => {
  const segment = (pathname || '').split('/').find(Boolean);
  if (!segment) return null;
  let slug;
  try {
    slug = decodeURIComponent(segment);
  } catch {
    return null;
  }
  return NON_STORE_SEGMENTS.has(slug) ? null : slug;
};

const sessionGet = (key) => {
  try {
    return sessionStorage.getItem(key);
  } catch {
    return null;
  }
};

const sessionSet = (key, value) => {
  try {
    sessionStorage.setItem(key, value);
    return true;
  } catch {
    return false;
  }
};

/**
 * Keeps the Google Ads click from a landing URL for 90 days together with
 * the store it landed on. A newer click replaces an older one (last click
 * wins, like Google's own cookies on this shared domain); a URL without a
 * valid click leaves the stored one alone.
 */
export const captureAdsClick = (search, pathname, now = Date.now()) => {
  const query = (search || '').startsWith('?') ? search.slice(1) : search || '';
  const params = new URLSearchParams(query);
  const type = CLICK_TYPES.find((t) => normalizeClickId(params.get(t)));
  if (!type) return null;
  const record = {
    type,
    value: normalizeClickId(params.get(type)),
    slug: landingSlugFromPath(pathname),
    ts: now,
  };
  safeSetItem(ADS_CLICK_KEY, JSON.stringify(record));
  return record;
};

const isClickRecord = (record) =>
  Boolean(record) &&
  CLICK_TYPES.includes(record.type) &&
  typeof record.value === 'string' &&
  normalizeClickId(record.value) === record.value &&
  (record.slug === null || (typeof record.slug === 'string' && record.slug !== '')) &&
  Number.isFinite(record.ts);

export const getStoredAdsClick = (now = Date.now()) => {
  const raw = safeGetItem(ADS_CLICK_KEY);
  if (!raw) return null;
  let record = null;
  try {
    record = JSON.parse(raw);
  } catch {
    record = null;
  }
  if (!isClickRecord(record) || now - record.ts > ADS_CLICK_TTL_MS) {
    safeRemoveItem(ADS_CLICK_KEY);
    return null;
  }
  return record;
};

/**
 * Fields Order/CreateOrderForCustomer takes for Ads attribution. The API
 * keeps them only when the landing store belongs to the order's company, so
 * a click that landed outside a store sends nothing.
 */
export const adsClickOrderFields = (now = Date.now()) => {
  const click = getStoredAdsClick(now);
  if (!click || !click.slug) return {};
  return { [click.type]: click.value, adslandingslug: click.slug };
};

export const conversionDedupKey = (transactionId) => `ga_conv_${transactionId}`;

export const ensureGtag = (conversionId) => {
  if (typeof window === 'undefined' || !conversionId) return false;
  window.dataLayer = window.dataLayer || [];
  if (typeof window.gtag !== 'function') {
    window.gtag = function gtag() {
      window.dataLayer.push(arguments);
    };
  }
  if (!document.getElementById(GTAG_SCRIPT_ID)) {
    const script = document.createElement('script');
    script.id = GTAG_SCRIPT_ID;
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${conversionId}`;
    document.head.appendChild(script);
    window.gtag('js', new Date());
  }
  if (window.__thiswayGtagId !== conversionId) {
    window.gtag('config', conversionId);
    window.__thiswayGtagId = conversionId;
  }
  return true;
};

export const firePurchaseConversion = ({
  conversionId,
  conversionLabel,
  value,
  transactionId,
}) => {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') {
    return false;
  }
  if (!conversionId || !conversionLabel || !transactionId) return false;
  const key = conversionDedupKey(transactionId);
  if (sessionGet(key)) return false;
  window.gtag('event', 'conversion', {
    send_to: `${conversionId}/${conversionLabel}`,
    value: Number(value) || 0,
    currency: 'USD',
    transaction_id: String(transactionId),
  });
  sessionSet(key, '1');
  return true;
};
